/* eslint-disable no-param-reassign */
import { BarSeriesOption, util } from 'echarts';
import { ThemeParser } from '../../../core/theme';
import { normalizeToArray } from '../../../util/utils';
import { FONT_SIZE_LIST, FONT_WEIGHT_LIST } from '../../mobile/static/font';
import type { PresetMode } from '../..';

/** 继承自移动端设计规范 */
const specification = {
  /** 最小柱高度 */
  barMinHeight: {
    stack: 0,
    normal: 0.5
  },
  /** 柱标签 */
  label: {
    /** 堆叠柱图 */
    stack: undefined,
    /** 柱图 */
    normal: {
      distance: 4,
      lineHeight: 12,
      dvPosition: 'valueDirection',
      fontWeight: FONT_WEIGHT_LIST.barLabel,
      fontSize: FONT_SIZE_LIST.seriesLabel
    }
  },
  /** 动画 */
  animation: [
    {
      /** 匹配条件 */
      condition: {
        /** 图最小高度 */
        minH: 0,
        /** 图最大高度 */
        maxH: 120
      },
      opt: {
        animationDuration: 320,
        animationDurationUpdate: 320,
        animationEasing: 'cubicOut',
        animationEasingUpdate: 'cubicOut'
      }
    },
    {
      /** 匹配条件 */
      condition: {
        /** 图最小高度 */
        minH: 120,
        /** 图最大高度 */
        maxH: 240
      },
      opt: {
        /** 动画时长 ms */
        animationDuration: 480,
        animationDurationUpdate: 480,
        animationEasing: 'cubicOut',
        animationEasingUpdate: 'cubicOut'
      }
    }
  ]
};

/**
 * 获取柱状图中同一类目下有几根柱子, 相同stack的为一根柱子, 非堆叠的单独占一根柱子
 * @param seriesOptions
 * @returns
 */
function getBarCountInGroup(seriesOptions: BarSeriesOption[]) {
  let barCountInGroup = 0;
  const stackMap = new Map<string, boolean>();
  (seriesOptions as []).forEach(item => {
    if (item.type !== 'bar') {
      return;
    }
    if (item.stack) {
      if (!stackMap.has(item.stack)) {
        stackMap.set(item.stack, true);
        barCountInGroup += 1;
      }
    } else {
      barCountInGroup += 1;
    }
  });

  return barCountInGroup;
}

function getDefaultBarSeries(
  option: Parameters<ThemeParser>[0],
  bar: BarSeriesOption,
  index: number,
  length: number,
  barCountInGroup: number,
  chart: Parameters<ThemeParser>[1],
  themeMode: PresetMode
): BarSeriesOption {
  // 柱状图细分类型：stack（堆叠柱图），normal（普通柱图）
  const subType = bar.stack ? 'stack' : 'normal';
  const isStack = subType === 'stack';
  const singleBarWidth = 32;
  const mutiBatWidth = 66 / barCountInGroup;
  return {
    /** 柱子标签超过五个不显示 */
    // @ts-ignore
    dvMaxLabelCount: 5,
    barCategoryGap: index === length - 1 ? '33%' : undefined,
    barGap: '5%',
    barMinHeight: specification.barMinHeight[subType],
    barMaxWidth: barCountInGroup === 1 ? singleBarWidth : mutiBatWidth,
    label: {
      ...specification.label[subType],
      show: isStack ? false : barCountInGroup < 2,
      color: themeMode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
      fontFamily: 'sans-serif'
    },
    emphasis: {
      disabled: true
    },
    animation: true
  };
}

function parseBarSeries(
  option: Parameters<ThemeParser>[0],
  seriesOptions: Parameters<ThemeParser>[0]['series'],
  chart: Parameters<ThemeParser>[1],
  themeMode: PresetMode
) {
  const barCountInGroup = getBarCountInGroup(seriesOptions as BarSeriesOption[]);
  /** 所有series中 类型为bar的数量 */
  const barSeriesCount = (seriesOptions as []).reduce(
    (total, item) => (item.type === 'bar' && item.dvType !== 'progress' ? total + 1 : total),
    0
  );
  (seriesOptions as []).forEach((series, index) => {
    // @ts-ignore
    if (series.type !== 'bar' || series.dvType === 'progress') {
      return;
    }
    const defaultOption = getDefaultBarSeries(
      option,
      series,
      index,
      barSeriesCount,
      barCountInGroup,
      chart,
      themeMode
    );

    seriesOptions[index] = util.merge(defaultOption, series, true);
  });
}

export function barThemeParse(...args: Parameters<ThemeParser>) {
  const [option, chart] = args;
  option.series = normalizeToArray(option.series);
  const themeMode = chart.getModel().getThemeName().includes('light') ? 'light' : 'dark';
  parseBarSeries(option, option.series, chart, themeMode);
}
